/**
 * Tiered-price RESOLUTION — the one answer to "what does one of this option
 * cost at this order quantity".
 *
 * # Why this moved here (Chapter 10, P4b)
 *
 * The rule used to live only in `apps/public/src/lib/pricing.ts`. P4b puts an
 * ITEMISED order table into both order emails, which the admin renders — and the
 * admin cannot import the public bundle. A second copy of this walk in
 * `apps/admin` would put the emailed line prices and the on-screen line prices
 * on separate implementations of the same rule, which is precisely the P3a
 * invariant ("the screen and the email agree to the cent") re-opened one layer
 * down.
 *
 * So the rule joins the band TABLE it reads (`pricingBands.ts`, moved here in
 * P2a for the same reason) and `apps/public`'s `getTieredPrice` becomes a
 * delegation. `apps/public/src/lib/pricing.test.ts` asserts the two are the same
 * function object, and `pricing.seededParity.test.ts` freezes every seeded price
 * as a golden master, so the move cannot change a customer's quote silently.
 */

import { TIER_BANDS, getActiveTier, type Tier } from './pricingBands.js';

/**
 * The minimal option shape price resolution reads.
 *
 * Structural, and every field OPTIONAL, for the reason documented on
 * `TierPricedOptionLike`: `apps/admin` compiles with `strict: false`, under
 * which zod's inferred `FlowDoc` marks every property optional. A required
 * field here would make the real document type unassignable in that workspace.
 */
export interface TieredPriceOptionLike {
  /** Flat price — the fallback when no band resolves. */
  readonly price?: number;
  /** Per-band prices, keyed by `Tier`. */
  readonly tieredPrices?: Readonly<Partial<Record<string, number | undefined>>>;
  /**
   * The smallest order quantity at which this option can be had at all. Below
   * it the option is not charged — see `isBelowMinBoxes`.
   */
  readonly minBoxes?: number;
}

/** Band keys, low to high — the order the downward walk relies on. */
const TIERS: readonly Tier[] = TIER_BANDS.map((b) => b.key);

/**
 * The per-unit price of an option at a given order quantity.
 *
 * Resolution order:
 *   1. No tier table at all → the flat `price`.
 *   2. Quantity below the lowest band floor → the flat `price`.
 *   3. Otherwise the active band's price, walking DOWN past any band that is
 *      absent or zero, and finally to the flat `price` if nothing is priced.
 *
 * Step 3's downward walk is what makes the two Chapter-10 upper bands a
 * no-migration change, and it is also the mechanism behind F3: an option whose
 * entire table is zeros resolves the flat price at every quantity. The publish
 * gate (`publishPricingGate.ts`) is what stops that reaching a customer.
 */
export function resolveTieredPrice(
  option: TieredPriceOptionLike,
  qty: number,
): number {
  if (!option.tieredPrices) return option.price || 0;

  const tier = getActiveTier(qty);
  if (!tier) return option.price || 0;

  const tierIndex = TIERS.indexOf(tier);

  for (let i = tierIndex; i >= 0; i--) {
    const price = option.tieredPrices[TIERS[i]!];
    if (price !== undefined && price !== 0) return price;
  }

  return option.price || 0;
}

/**
 * True when the order quantity is below this option's own minimum — i.e. the
 * customer cannot have this item on this order.
 *
 * Such an option is charged NOTHING (`computeUnitBoxPrice` skips it entirely),
 * so anything that itemises an order must gate on this or it will bill for a
 * thing the order does not include. A `minBoxes` of 0 means "no minimum".
 */
export function isBelowMinBoxes(
  option: TieredPriceOptionLike,
  qty: number,
): boolean {
  return !!option.minBoxes && qty < option.minBoxes;
}
